import React from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Card, CardContent, Divider, Button } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import LaunchIcon from "@mui/icons-material/Launch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplyIcon from "@mui/icons-material/Reply";
import VideocamIcon from "@mui/icons-material/Videocam";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
/**
 * TemplateCreationPreview
 * Renders a preview of the current template (header, body, footer, and buttons).
 */
const TemplateCreationPreview = () => {
  // Select template data from your Redux store (utilitySlice, for instance)
  const { 
    header, 
    headerData, 
    body, 
    footer, 
    buttons, 
    carouselCards,
    selectedCategory,
    codeDeliveryMethod,
    addSecurityRecommendation,
    addExpiryTime,
    expiryMinutes,
  } = useSelector((state) => state.templateCreation);

  const { quickReplies, phoneButtons, linkButtons, copyCodeButton } = buttons;

  // Check if this is an authentication template
  const isAuthenticationTemplate = selectedCategory === "Authentication";

  // Generate sample verification code for authentication templates
  const sampleCode = "123456";

  // Get button text based on delivery method
  const getButtonText = (method) => {
    switch (method) {
      case "COPY_CODE":
        return "Copy code";
      case "ONE_TAP":
        return "Verify";
      case "ZERO_TAP":
        return "Verify";
      default:
        return "Copy code";
    }
  };

  // Render authentication template preview
  if (isAuthenticationTemplate) {
    return (
      <Card
        sx={{
          width: 250,
          height: "100%",
          
         
        
        }}
      >
        <CardContent sx={{ padding: "16px", pb: "12px !important" }}>
          {/* Authentication Message Content */}
          <Box sx={{ marginBottom: 2 }}>
            {/* Verification Code */}
            <Typography
              variant="body1"
              sx={{
                color: (theme) => theme.palette.text.primary,
                fontWeight: 500,
                marginBottom: 1,
                lineHeight: 1.4,
              }}
            >
              <strong>*{sampleCode}*</strong> is your verification code.
            </Typography>

            {/* Security Recommendation */}
            {addSecurityRecommendation && (
              <Typography
                variant="body2"
                sx={{
                  color: "#666666",
                  fontSize: "14px",
                  lineHeight: 1.3,
                  marginBottom: 1,
                }}
              >
                For your security, do not share this code.
              </Typography>
            )}

            {/* Expiry Time (if enabled) */}
            {addExpiryTime && (
              <Typography
                variant="body2"
                sx={{
                  color: "#666666",
                  fontSize: "14px",
                  lineHeight: 1.3,
                  fontStyle: "italic",
                }}
              >
                This code will expire in {expiryMinutes} minutes.
              </Typography>
            )}
          </Box>

          {/* Copy Code Button */}
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            sx={{
              width: "100%",
              borderRadius: "8px",
              borderColor: "#25D366",
              color: "#25D366",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              padding: "8px 16px",
              "&:hover": {
                borderColor: "#128C7E",
                backgroundColor: "rgba(37, 211, 102, 0.04)",
              },
            }}
          >
            {getButtonText(codeDeliveryMethod)}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Regular template preview (existing code)
  return (
    <Card
      sx={{
        width: 250,
        height: "100%",
        borderRadius: "8px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.background.paper,
        padding: 0,
        "& .MuiCardContent-root": {
          padding: 0,
        },
      }}
    >
      <CardContent sx={{ padding: 0 }}>
        {" "}
        {/* Remove padding from card content */}
        {/* Header */}
        {header && header === "text" ? (
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              padding: "10px 0 0 10px",
              color: (theme) => theme.palette.text.primary,
            }}
          >
            {headerData}
          </Typography>
        ) : header && header === "image" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <ImageIcon
              sx={{
                fontSize: 30,
                color: (theme) => theme.palette.text.secondary,
                padding: "50px",
              }}
            />
          </Box>
        ) : header && header === "video" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <VideocamIcon
              sx={{
                fontSize: 30,
                color: (theme) => theme.palette.text.secondary,
                padding: "50px",
              }}
            />
          </Box>
        ) : header && header === "document" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <PictureAsPdfIcon
              sx={{
                fontSize: 30,
                color: (theme) => theme.palette.text.secondary,
                padding: "50px",
              }}
            />
          </Box>
        ) : null}
        {/* Body */}
        <Typography
          variant="body2"
          sx={{
            padding: "5px 0 0 10px",
            color: (theme) => theme.palette.text.primary,
            whiteSpace: "pre-wrap",
          }}
        >
          {body}
        </Typography>
        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            padding: 1,
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          {footer}
        </Typography>
        {/* Carousel Cards - Show a single icon when carouselCards exist */}
        {carouselCards && carouselCards.length > 0 && (
          <>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 1,
              }}
            >
              <ViewCarouselIcon
                sx={{
                  fontSize: 30,
                  color: (theme) => theme.palette.primary.main,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  fontWeight: "medium",
                  ml: 1,
                }}
              >
                Carousel ({carouselCards.length} cards)
              </Typography>
            </Box>
          </>
        )}
        {/* <Divider /> */}
        {/* Quick Reply Buttons */}
        {quickReplies.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              padding: 1,
            }}
          >
            {quickReplies.map((qr, index) => (
              <Typography
                key={index}
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReplyIcon sx={{ marginRight: 1 }} />
                {qr.label || "Quick Reply Button"}
              </Typography>
            ))}
          </Box>
        )}
        {quickReplies.length > 0 && <Divider />}
        {/* Phone Buttons */}
        {phoneButtons.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              padding: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CallIcon sx={{ marginRight: 1 }} />
              {phoneButtons[0].label || "Call Button"}
            </Typography>
          </Box>
        )}
        {phoneButtons.length > 0 && <Divider />}
        {/* Link Buttons */}
        {linkButtons.length > 0 && (
          <Box sx={{ padding: 1 }}>
            {linkButtons.map((btn, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LaunchIcon sx={{ marginRight: 1 }} />
                {btn.label || "Link Button"}
              </Typography>
            ))}
          </Box>
        )}
        {linkButtons.length > 0 && <Divider />}
        {/* Copy Code Button */}
        {copyCodeButton && (
          <Box sx={{ padding: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ContentCopyIcon sx={{ marginRight: 1 }} />
              {copyCodeButton.offerCode || "Copy Code Button"}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TemplateCreationPreview;
